/**
 * Helper type for any action that can succeed of fail.
 *
 * ```typescript
 * // example with 'match()' from ts-pattern
 * const res = Result.Ok({ test: true });
 * match(res)
 * 	.with({ success: true }, ({ data }) => console.log(data.test))
 * 	.with({ success: false }, ({ error }) => console.log(error))
 * 	.exhaustive();
 * ```
 */
export type Result<TData = never, TError = string> =
	| { success: true; data?: TData }
	| { success: false; error?: TError };

export const Result = {
	Ok: <TData>(data?: TData): Result<TData, never> => ({ success: true, data }),
	Error: <TError>(error?: TError): Result<never, TError> => ({ success: false, error }),
};

export type AsyncResult<TError = string, TData = never> = Promise<Result<TData, TError>>;

export const AsyncResult = {
	Ok: <TData>(data?: TData): AsyncResult<never, TData> => Promise.resolve(Result.Ok(data)),
	Error: <TError>(error?: TError): AsyncResult<TError, never> => Promise.resolve(Result.Error(error)),
	fromPromise: <T>(promise: Promise<T>) => {
		// TODO implement
	},
};
